import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect, useRef } from 'react';
import { getCurrentUser, fetchUserAttributes, updateUserAttributes } from 'aws-amplify/auth';
import DealsManager from './DealsManager';
import { useImageModeration } from '../hooks/useImageModeration';
import LocationAutocomplete from '../components/LocationAutocomplete';
import '../App.css';

const API_URL = 'https://opmlee572h.execute-api.us-west-2.amazonaws.com';

const SERVICE_TYPES = [
  '✂️ Barber', '💇 Hair Stylist', '🪒 Beard Grooming Specialist', '💇‍♀️ Hair Extension Specialist',
  '💅 Nail Tech', '🦶 Pedicure Specialist', '🧴 Esthetician / Skincare', '🧖 Facial Specialist',
  '💉 Cosmetic Injector (Botox / Fillers)', '🎨 Permanent Makeup (PMU)', '🧴 Spray Tan Artist',
  '💄 Makeup Artist', '🌿 Lash Technician', '💈 Waxing', '🦷 Teeth Whitening', '🪡 Braids / Locs',
  '🎨 Tattoo Artist', '🚗 Auto Detailer', '🧽 Mobile Car Wash', '🔧 Mechanic', '🛞 Tire Service',
  '🎵 Car Audio Installer', '🚘 Wrap / Tint Specialist', '🧹 House Cleaner', '🪟 Window Cleaner',
  '🧱 Pressure Washer', '🌿 Gardener / Landscaper', '❄️ HVAC Technician', '⚡ Electrician',
  '🚿 Plumber', '🪵 Furniture Assembler', '🛋️ Carpet Cleaner', '🔧 Handyman',
  '📸 Photographer', '🎥 Videographer', '🎬 Content Creator (TikTok/Reels)', '🖥️ Video Editor',
  '🎨 Graphic Designer', '🧠 Branding Consultant', '🌐 Website Builder', '☁️ Cloud Setup (AWS etc)',
  '🔐 IT Support', '📱 App Developer', '🛍️ Shopify / E-commerce Setup',
  '🏋️ Personal Trainer', '🧘 Yoga Instructor', '🥗 Nutrition Coach', '🧠 Life Coach',
  '🏃 Running Coach', '🏀 Sports Trainer', '🧑‍🏫 Tutor', '📚 Test Prep Tutor (SAT etc)',
  '💻 Coding Tutor', '🎸 Music Producer Coach', '🎤 Vocal Coach', '🗣️ Language Tutor',
  '🎵 Music Instructor', '🎧 DJ', '🎤 Host / MC', '🎉 Event Planner',
  '🍹 Bartender (Private Events)', '🎈 Party Decorator', '🍽️ Private Chef',
  '🐾 Pet Groomer', '🐕 Dog Walker', '🛒 Personal Shopper', '🧳 Travel Planner',
  '👗 Fashion Stylist', '👶 Babysitter', '🧸 Nanny (Full-Time Childcare)',
  '🏫 After-School Care', '💤 Overnight Babysitting', '📞 Missed Call Assistant',
  '🧼 Closet Organizer', '📦 Moving Help', '🧾 Resume Writer', 'Other'
];

const TIPS = [
  { icon: '📸', text: 'Profiles with clear photos get 3x more bookings' },
  { icon: '✍️', text: 'A detailed bio builds trust with new clients' },
  { icon: '📍', text: 'Adding your location helps clients find you nearby' },
  { icon: '⭐', text: 'Top profiles with bios appear on page 1' },
];

function ProviderSettings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [userId, setUserId] = useState('');
  const userIdRef = useRef('');
  const [profile, setProfile] = useState({ name: '', bio: '', location: '', service_type: '' });
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const profilePicUrlRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [servicesSaved, setServicesSaved] = useState(false);
  const [onlinePaymentOnly, setOnlinePaymentOnly] = useState(false);
  const [connectStatus, setConnectStatus]   = useState(null);
  const [connectLoading, setConnectLoading] = useState(false);
  const [balance, setBalance]               = useState(null);
  const [balanceLoading, setBalanceLoading] = useState(false);
  const [payoutAmount, setPayoutAmount]     = useState('');
  const [payoutLoading, setPayoutLoading]   = useState(false);
  const [payoutResult, setPayoutResult]     = useState(null);
  const [payoutError, setPayoutError]       = useState('');
  const [providerEmail, setProviderEmail]   = useState('');
  const [verified, setVerified]             = useState(false);
  const [verifyLoading, setVerifyLoading]   = useState(false);
  const [services, setServices] = useState([]);
  const servicesRef = useRef([]);
  const [newService, setNewService] = useState({
    name: '', price: '', duration: '', description: '',
    recurring: false, interval: 'month'
  });
  const [editingService, setEditingService] = useState(null);
  const [svcMediaUploading, setSvcMediaUploading] = useState(false);
  const [svcMediaError, setSvcMediaError] = useState('');
  const svcMediaInputRef = useRef(null);
  const [serviceSearch, setServiceSearch] = useState('');
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const serviceRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaInputRef = useRef(null);
  const [mediaUrls, setMediaUrls] = useState([]);
  const mediaUrlsRef = useRef([]);
  const [mediaUploading, setMediaUploading] = useState(false);
  const [mediaUploadProgress, setMediaUploadProgress] = useState(null);
  const [mediaError, setMediaError] = useState('');
  const dragIndexRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);

  // ── Image moderation hook ────────────────────────────────────────────────
  const { checkImage, error: modError } = useImageModeration();

  useEffect(() => {
    getCurrentUser().then((user) => {
      const uid = user.userId;
      setUserId(uid);
      userIdRef.current = uid;
      fetchUserAttributes().then((attrs) => {
        setProfile({
          name: attrs.name || '',
          bio: attrs['custom:bio'] || '',
          location: attrs['custom:location'] || '',
          service_type: attrs['custom:service_type'] || '',
        });
        setProviderEmail(attrs.email || '');
      });
      fetch(`${API_URL}/providers?providerId=${uid}`)
        .then(res => res.json())
        .then(data => {
          if (data.profilePicUrl) {
            setProfilePicUrl(data.profilePicUrl);
            profilePicUrlRef.current = data.profilePicUrl;
          }
          if (data.services && Array.isArray(data.services)) {
            setServices(data.services);
            servicesRef.current = data.services;
          }
          if (data.mediaUrls && Array.isArray(data.mediaUrls)) {
            setMediaUrls(data.mediaUrls);
            mediaUrlsRef.current = data.mediaUrls;
          }
          if (data.onlinePaymentOnly) setOnlinePaymentOnly(true);
          if (data.verified) setVerified(true);
          fetchConnectStatus(uid);
        })
        .catch(err => console.error(err));
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('connect') === 'success' || params.get('connect') === 'refresh') {
      setActiveTab('payments');
      window.history.replaceState({}, '', window.location.pathname);
    }
    if (params.get('identity') === 'complete') {
      setActiveTab('payments');
      window.history.replaceState({}, '', window.location.pathname);
      setTimeout(async () => {
        try {
          const uid = userIdRef.current;
          if (!uid) return;
          const res = await fetch(`${API_URL}/identity/status?providerId=${uid}`);
          const data = await res.json();
          if (data.verified) setVerified(true);
        } catch {}
      }, 2000);
    }
  }, []);

  useEffect(() => {
    function handleClickOutside(e) {
      if (serviceRef.current && !serviceRef.current.contains(e.target)) {
        setShowServiceDropdown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchConnectStatus = async (uid) => {
    const id = uid || userIdRef.current;
    if (!id) return;
    try {
      const res  = await fetch(`${API_URL}/connect/status?providerId=${id}`);
      const data = await res.json();
      setConnectStatus(data);
      if (data.onboardingComplete) fetchBalance(id);
    } catch (err) { console.error(err); }
  };

  const fetchBalance = async (uid) => {
    const id = uid || userIdRef.current;
    if (!id) return;
    setBalanceLoading(true);
    try {
      const res  = await fetch(`${API_URL}/connect/balance?providerId=${id}`);
      const data = await res.json();
      if (!data.error) setBalance(data);
    } catch (err) { console.error(err); }
    setBalanceLoading(false);
  };

  const handleConnectStripe = async () => {
    setConnectLoading(true);
    try {
      const res  = await fetch(`${API_URL}/connect/onboard`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId: userIdRef.current, email: providerEmail, providerName: profile.name }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else alert('Error starting Stripe setup: ' + (data.error || 'Unknown error'));
    } catch (err) { alert('Error: ' + err.message); }
    setConnectLoading(false);
  };

  const handleOpenStripeDashboard = async () => {
    try {
      const res  = await fetch(`${API_URL}/connect/dashboard?providerId=${userIdRef.current}`);
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank');
    } catch (err) { alert('Error opening dashboard: ' + err.message); }
  };

  const handleGetVerified = async () => {
    setVerifyLoading(true);
    try {
      const res  = await fetch(`${API_URL}/identity/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId: userIdRef.current }),
      });
      const data = await res.json();
      if (data.alreadyVerified) { setVerified(true); setVerifyLoading(false); return; }
      if (data.url) window.location.href = data.url;
      else alert('Error starting verification: ' + (data.error || 'Unknown error'));
    } catch (err) { alert('Error: ' + err.message); }
    setVerifyLoading(false);
  };

  const handleRequestPayout = async (instant = false) => {
    const amt = parseFloat(payoutAmount);
    if (!amt || amt <= 0) { setPayoutError('Enter a valid amount'); return; }
    if (balance && amt > balance.available) { setPayoutError(`Maximum available is $${balance.available.toFixed(2)}`); return; }
    setPayoutLoading(true); setPayoutError(''); setPayoutResult(null);
    try {
      const res  = await fetch(`${API_URL}/connect/payout`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ providerId: userIdRef.current, amount: amt, instant }),
      });
      const data = await res.json();
      if (res.ok) {
        setPayoutResult(data);
        setPayoutAmount('');
        fetchBalance(userIdRef.current);
      } else {
        if (data.code === 'instant_unsupported') setPayoutError('Instant payouts require a debit card on your Stripe account. Add one in your Stripe dashboard.');
        else setPayoutError(data.error || 'Payout failed');
      }
    } catch (err) { setPayoutError('Error: ' + err.message); }
    setPayoutLoading(false);
  };

  const saveProviderToDB = async (updatedProfile, updatedServices, picUrl, updatedMediaUrls, payOnlineOnly) => {
    const id = userIdRef.current;
    if (!id) return;
    const pic = picUrl || profilePicUrlRef.current || '';
    const svcs = updatedServices || servicesRef.current;
    const derivedServiceType = svcs.find(s => s.serviceType)?.serviceType || updatedProfile.service_type || '';
    const onlineOnly = payOnlineOnly !== undefined ? payOnlineOnly : onlinePaymentOnly;
    await fetch(`${API_URL}/providers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        providerId: id,
        name: updatedProfile.name,
        email: updatedProfile.email || '',
        bio: updatedProfile.bio,
        location: updatedProfile.location,
        service_type: derivedServiceType,
        serviceType: derivedServiceType,
        services: svcs,
        profilePicUrl: pic,
        onlinePaymentOnly: onlineOnly,
      }),
    });
  };

  // ── Profile pic upload with moderation ───────────────────────────────────
  const handleProfilePicUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploadError('');
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) { setUploadError('Only JPG, PNG, or WebP images are allowed.'); return; }
    if (file.size > 5 * 1024 * 1024) { setUploadError('Image must be under 5MB.'); return; }

    // ── Content moderation check ─────────────────────────────────────────
    setUploading(true);
    setUploadError('Scanning image for content policy...');
    const safe = await checkImage(file);
    if (!safe) {
      setUploadError('⚠️ This image was flagged for inappropriate content and cannot be uploaded as a profile photo. Please use a professional headshot or business photo.');
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setUploadError('');
    // ── Proceed with upload ──────────────────────────────────────────────
    try {
      const reader = new FileReader();
      reader.onload = async (ev) => {
        try {
          const base64 = ev.target.result.split(',')[1];
          const res = await fetch(`${API_URL}/upload-image`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ imageData: base64, contentType: file.type, userId: userIdRef.current }),
          });
          const data = await res.json();
          if (data.error && data.error.includes('inappropriate')) {
            // Server-side Rekognition also caught it
            setUploadError('⚠️ ' + data.error);
          } else if (data.publicUrl) {
            setProfilePicUrl(data.publicUrl);
            profilePicUrlRef.current = data.publicUrl;
            await saveProviderToDB(profile, services, data.publicUrl);
          } else {
            setUploadError('Upload failed.');
          }
        } catch { setUploadError('Upload failed.'); }
        setUploading(false);
      };
      reader.readAsDataURL(file);
    } catch { setUploadError('Upload failed.'); setUploading(false); }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await updateUserAttributes({
        userAttributes: { name: profile.name, 'custom:bio': profile.bio, 'custom:location': profile.location }
      });
      await saveProviderToDB(profile, services);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleDeleteProfile = async () => {
    if (!window.confirm('Delete your provider profile? This will permanently remove your listing, all services, and all deals. This cannot be undone.')) return;
    try {
      await fetch(`${API_URL}/providers?providerId=${userId}`, { method: 'DELETE' });
      window.location.href = '/dashboard';
    } catch {
      alert('Failed to delete profile. Please try again.');
    }
  };

  const handleSaveServiceType = async () => {
    try {
      await updateUserAttributes({ userAttributes: { 'custom:service_type': profile.service_type } });
      await saveProviderToDB(profile, services);
      setServicesSaved(true);
      setTimeout(() => setServicesSaved(false), 3000);
    } catch (err) { console.error(err); }
  };

  const handleAddService = async () => {
    if (!newService.name || !newService.price) return;
    const svc = { ...newService, id: Date.now(), interval: newService.recurring ? newService.interval : null };
    const updated = [...services, svc];
    setServices(updated);
    servicesRef.current = updated;
    setNewService({ name: '', price: '', duration: '', description: '', recurring: false, interval: 'month' });
    await saveProviderToDB(profile, updated);
  };

  const handleDeleteService = async (id) => {
    const updated = services.filter(s => s.id !== id);
    setServices(updated);
    servicesRef.current = updated;
    await saveProviderToDB(profile, updated);
  };

  // ── Media upload with moderation ─────────────────────────────────────────
  const handleMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setMediaError('');
    const allowedTypes = ['image/jpeg','image/png','image/webp','image/gif','video/mp4','video/quicktime','video/webm'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) { setMediaError(`${file.name}: only JPG, PNG, WebP, GIF, MP4, MOV or WebM allowed.`); return; }
      if (file.type.startsWith('video/') && file.size > 50*1024*1024) { setMediaError(`${file.name}: videos must be under 50MB.`); return; }
      if (!file.type.startsWith('video/') && file.size > 8*1024*1024) { setMediaError(`${file.name}: images must be under 8MB.`); return; }
    }
    if (mediaUrlsRef.current.length + files.length > 20) { setMediaError('Maximum 20 media items allowed.'); return; }

    // Moderate images before upload
    for (const file of files) {
      if (!file.type.startsWith('video/')) {
        setMediaError(`Scanning ${file.name}...`);
        const safe = await checkImage(file);
        if (!safe) {
          setMediaError(`⚠️ ${file.name} was flagged for inappropriate content and cannot be uploaded.`);
          if (mediaInputRef.current) mediaInputRef.current.value = '';
          return;
        }
      }
    }
    setMediaError('');

    setMediaUploading(true);
    const newUrls = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setMediaUploadProgress({ name: file.name, idx: i+1, total: files.length });
      try {
        const isVideo = file.type.startsWith('video/');
        const ext = file.name.split('.').pop().toLowerCase();
        const reader = new FileReader();
        const base64 = await new Promise((res,rej) => { reader.onload = ev => res(ev.target.result.split(',')[1]); reader.onerror = rej; reader.readAsDataURL(file); });
        const response = await fetch(`${API_URL}/upload-media`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ mediaData: base64, contentType: file.type, userId: userIdRef.current, ext }) });
        const data = await response.json();
        if (data.error && data.error.includes('inappropriate')) {
          setMediaError('⚠️ ' + data.error);
          break;
        }
        if (data.publicUrl) newUrls.push({ url: data.publicUrl, type: isVideo ? 'video' : 'image', name: file.name });
      } catch(err) { setMediaError(`Failed to upload ${file.name}.`); }
    }
    const updated = [...mediaUrlsRef.current, ...newUrls];
    setMediaUrls(updated); mediaUrlsRef.current = updated;
    await saveProviderToDB(profile, services, null, updated);
    setMediaUploading(false); setMediaUploadProgress(null);
    if (mediaInputRef.current) mediaInputRef.current.value = '';
  };

  const handleDeleteMedia = async (idx) => {
    const updated = mediaUrlsRef.current.filter((_,i) => i !== idx);
    setMediaUrls(updated); mediaUrlsRef.current = updated;
    await saveProviderToDB(profile, services, null, updated);
  };

  // ── Service media upload with moderation ─────────────────────────────────
  const handleSvcMediaUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setSvcMediaError('');
    const allowedTypes = ['image/jpeg','image/png','image/webp','image/gif','video/mp4','video/quicktime','video/webm'];
    for (const file of files) {
      if (!allowedTypes.includes(file.type)) { setSvcMediaError(`${file.name}: only JPG, PNG, WebP, GIF, MP4, MOV, WebM allowed.`); return; }
      if (file.type.startsWith('video/') && file.size > 6*1024*1024) { setSvcMediaError(`Videos must be under 6MB for now. Compress your video first.`); return; }
      if (!file.type.startsWith('video/') && file.size > 4*1024*1024) { setSvcMediaError(`Images must be under 4MB. Try compressing or resizing first.`); return; }
    }
    const existing = editingService.mediaUrls || [];
    if (existing.length + files.length > 12) { setSvcMediaError('Max 12 items per service.'); return; }

    // Moderate images
    for (const file of files) {
      if (!file.type.startsWith('video/')) {
        setSvcMediaError(`Scanning ${file.name}...`);
        const safe = await checkImage(file);
        if (!safe) {
          setSvcMediaError(`⚠️ ${file.name} was flagged for inappropriate content and cannot be uploaded.`);
          if (svcMediaInputRef.current) svcMediaInputRef.current.value = '';
          return;
        }
      }
    }
    setSvcMediaError('');

    setSvcMediaUploading(true);
    const newUrls = [...existing];
    for (const file of files) {
      try {
        const isVideo = file.type.startsWith('video/');
        const ext = file.name.split('.').pop().toLowerCase();
        const reader = new FileReader();
        const base64 = await new Promise((res, rej) => { reader.onload = ev => res(ev.target.result.split(',')[1]); reader.onerror = rej; reader.readAsDataURL(file); });
        const response = await fetch(`${API_URL}/upload-media`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mediaData: base64, contentType: file.type, userId: userIdRef.current, ext }),
        });
        const data = await response.json();
        if (data.error && data.error.includes('inappropriate')) {
          setSvcMediaError('⚠️ ' + data.error);
          break;
        }
        if (data.publicUrl) newUrls.push({ url: data.publicUrl, type: isVideo ? 'video' : 'image' });
        else setSvcMediaError(`Upload failed: ${data.error || 'Unknown error.'}`);
      } catch (err) { setSvcMediaError(`Failed to upload ${file.name}.`); }
    }
    setEditingService(prev => ({ ...prev, mediaUrls: newUrls }));
    setSvcMediaUploading(false);
    if (svcMediaInputRef.current) svcMediaInputRef.current.value = '';
  };

  const handleDeleteSvcMedia = (idx) => {
    setEditingService(prev => ({ ...prev, mediaUrls: (prev.mediaUrls || []).filter((_, i) => i !== idx) }));
  };

  const handleDragStart = (e, idx) => {
    dragIndexRef.current = idx;
    e.dataTransfer.effectAllowed = 'move';
    setTimeout(() => { e.target.style.opacity = '0.4'; }, 0);
  };
  const handleDragEnd = (e) => {
    e.target.style.opacity = '1';
    dragIndexRef.current = null;
    setDragOver(null);
  };
  const handleDragEnter = (idx) => { if (idx !== dragIndexRef.current) setDragOver(idx); };
  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    const fromIdx = dragIndexRef.current;
    if (fromIdx === null || fromIdx === dropIdx) return;
    const arr = [...(editingService.mediaUrls || [])];
    const [moved] = arr.splice(fromIdx, 1);
    arr.splice(dropIdx, 0, moved);
    setEditingService(prev => ({ ...prev, mediaUrls: arr }));
    dragIndexRef.current = null;
    setDragOver(null);
  };

  const openNewService = () => { setSvcMediaError(''); setDragOver(null); setEditingService({ name: '', price: '', duration: '', description: '', recurring: false, interval: 'month', mediaUrls: [] }); };
  const openEditService = (svc) => { setSvcMediaError(''); setDragOver(null); setEditingService({ ...svc, mediaUrls: svc.mediaUrls || [] }); };

  const handleSaveEditingService = async () => {
    if (!editingService.name || !editingService.price) return;
    if (editingService.serviceType) {
      const duplicate = services.find(s => s.serviceType === editingService.serviceType && s.id !== editingService.id);
      if (duplicate) { setSvcMediaError(`You already have a "${editingService.serviceType}" service. Each category can only be added once.`); return; }
    }
    let updated;
    if (editingService.id) {
      updated = services.map(s => s.id === editingService.id ? { ...editingService } : s);
    } else {
      updated = [...services, { ...editingService, id: Date.now(), interval: editingService.recurring ? editingService.interval : null }];
    }
    setServices(updated);
    servicesRef.current = updated;
    setEditingService(null);
    await saveProviderToDB(profile, updated);
  };

  const intervalLabel = (interval) => {
    if (interval === 'week') return 'Weekly';
    if (interval === 'biweek') return 'Bi-weekly';
    return 'Monthly';
  };

  const completionItems = [
    { done: !!profilePicUrl, label: 'Profile photo' },
    { done: !!profile.name, label: 'Name' },
    { done: !!profile.location, label: 'Location' },
    { done: !!profile.bio && profile.bio.length > 10, label: 'Bio' },
    { done: services.length > 0, label: 'At least one service' },
    { done: services.some(s => s.serviceType), label: 'Service category set' },
  ];
  const completionPct = Math.round((completionItems.filter(i => i.done).length / completionItems.length) * 100);

  const s = {
    card: { background: 'rgba(255,255,255,0.6)', backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)', border: '1px solid rgba(255,255,255,0.82)', borderRadius: '20px', padding: '1.75rem', boxShadow: '0 4px 24px rgba(3,54,111,0.07), 0 1px 0 rgba(255,255,255,0.9) inset' },
    label: { fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem', display: 'block' },
    input: { width: '100%', background: 'var(--cream)', border: '1.5px solid var(--border)', borderRadius: '9px', padding: '0.8rem 1rem', color: 'var(--text)', fontSize: '0.925rem', outline: 'none', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>

      {/* Switch to Client */}
      <div onClick={() => navigate('/client-dashboard')}
        style={{ background: 'linear-gradient(135deg, var(--brand-1) 0%, var(--brand-3) 100%)', borderRadius: '18px', padding: '1.25rem 1.75rem', display: 'flex', alignItems: 'center', gap: '1.25rem', cursor: 'pointer', transition: 'all 0.22s', boxShadow: '0 4px 20px rgba(13,107,110,0.25)', marginBottom: '1.75rem', position: 'relative', overflow: 'hidden' }}
        onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 10px 30px rgba(13,107,110,0.38)'; }}
        onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(13,107,110,0.25)'; }}
      >
        <div style={{ position: 'absolute', top: -30, right: -30, width: 130, height: 130, background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ width: 44, height: 44, borderRadius: '12px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>👤</div>
        <div style={{ flex: 1 }}>
          <p style={{ fontFamily: 'Playfair Display, serif', fontWeight: 800, fontSize: '0.95rem', color: 'white', marginBottom: '0.15rem' }}>Switch to Client View</p>
          <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)' }}>Browse providers and manage your own bookings</p>
        </div>
        <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '1.1rem' }}>›</span>
      </div>

      {/* Page header */}
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.75rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.5rem' }}>
          Set up your profile to start getting bookings
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <div style={{ flex: 1, background: 'var(--cream-mid)', borderRadius: '99px', height: '10px', overflow: 'hidden', maxWidth: '400px' }}>
            <div style={{ height: '100%', width: `${completionPct}%`, background: 'var(--green)', borderRadius: '99px', transition: 'width 0.5s' }} />
          </div>
          <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--green)', whiteSpace: 'nowrap' }}>Complete your profile: {completionPct}%</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.75rem', borderBottom: '1px solid var(--border)', paddingBottom: '0' }}>
        {[
          { key: 'profile',  icon: '👤', label: 'Profile' },
          { key: 'services', icon: '✦',  label: 'Services' },
          { key: 'deals',    icon: '🏷️', label: 'Deals' },
          { key: 'payments', icon: '💳', label: 'Payments', badge: connectStatus?.onboardingComplete ? '✓' : !connectStatus?.connected ? '!' : null },
        ].map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            style={{ background: 'transparent', border: 'none', borderBottom: activeTab === tab.key ? '2px solid var(--green)' : '2px solid transparent', color: activeTab === tab.key ? 'var(--green)' : 'var(--text-muted)', fontFamily: 'DM Sans, sans-serif', fontSize: '0.9rem', fontWeight: 700, padding: '0.6rem 1.25rem', cursor: 'pointer', transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '-1px' }}>
            <span style={{ fontSize: '0.9rem' }}>{tab.icon}</span> {tab.label}
            {tab.badge && (
              <span style={{ width: 16, height: 16, borderRadius: '50%', background: tab.badge === '✓' ? 'var(--green)' : '#d97706', color: 'white', fontSize: '0.55rem', fontWeight: 800, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── PROFILE TAB ── */}
      {activeTab === 'profile' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: '1.5rem', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ ...s.card, display: 'grid', gridTemplateColumns: '200px 1fr', gap: '2rem', alignItems: 'start' }}>
              {/* Profile pic */}
              <div>
                <span style={s.label}>Profile Card</span>
                <div onClick={() => fileInputRef.current?.click()}
                  style={{ width: '100%', aspectRatio: '3/4', borderRadius: '12px', overflow: 'hidden', background: profilePicUrl ? 'transparent' : 'var(--green-pale)', border: '2px dashed rgba(26,92,58,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'relative', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--green)'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = 'rgba(26,92,58,0.25)'}>
                  {profilePicUrl
                    ? <img src={profilePicUrl} alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    : <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <div style={{ fontSize: '2rem', marginBottom: '0.5rem', opacity: 0.4 }}>📷</div>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>Click to upload photo</p>
                      </div>
                  }
                  {profilePicUrl && (
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.4)'; e.currentTarget.querySelector('span').style.opacity = '1'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(0,0,0,0)'; e.currentTarget.querySelector('span').style.opacity = '0'; }}>
                      <span style={{ opacity: 0, color: 'white', fontSize: '0.8rem', fontWeight: 700, transition: 'opacity 0.2s' }}>Change Photo</span>
                    </div>
                  )}
                </div>
                <input ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleProfilePicUpload} style={{ display: 'none' }} />
                {uploading && <p style={{ fontSize: '0.75rem', color: 'var(--green)', marginTop: '0.5rem', fontWeight: 600 }}>{uploadError || 'Uploading...'}</p>}
                {!uploading && uploadError && <p style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.5rem' }}>{uploadError}</p>}
                <p style={{ fontSize: '0.7rem', color: 'var(--text-light)', marginTop: '0.5rem', textAlign: 'center' }}>JPG, PNG or WebP · Max 5MB</p>
                <div style={{ marginTop: '1rem', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: '10px', padding: '0.875rem', textAlign: 'center' }}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 800, color: 'var(--text)', fontFamily: 'Playfair Display, serif' }}>{profile.name || 'Your Name'}</p>
                  {profile.location && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>📍 {profile.location}</p>}
                  {profile.bio && <p style={{ fontSize: '0.72rem', color: 'var(--text-mid)', marginTop: '0.5rem', lineHeight: 1.5, fontStyle: 'italic' }}>"{profile.bio.slice(0,80)}{profile.bio.length>80?'…':''}"</p>}
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', marginTop: '0.6rem', flexWrap: 'wrap' }}>
                    <span style={{ background: 'var(--green-pale)', color: 'var(--green)', border: '1px solid rgba(26,92,58,0.2)', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.6rem' }}>150+ Bookings</span>
                    <span style={{ background: '#fef3c7', color: '#92400e', border: '1px solid rgba(217,119,6,0.2)', borderRadius: '20px', fontSize: '0.65rem', fontWeight: 700, padding: '0.15rem 0.6rem' }}>⭐ 5.0</span>
                  </div>
                </div>
              </div>

              {/* Info fields */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <div><span style={s.label}>Add Your Info</span></div>
                <div>
                  <label style={{ ...s.label, marginBottom: '0.4rem' }}>Your Name <span style={{ color: 'var(--text-light)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(shown to clients)</span></label>
                  <input type="text" value={profile.name} onChange={e => setProfile({ ...profile, name: e.target.value })} placeholder="Biaghi Favela" style={s.input}
                    onFocus={e => { e.target.style.borderColor = 'var(--green)'; e.target.style.boxShadow = '0 0 0 3px rgba(26,92,58,0.1)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>

                {/* ── Location with autocomplete ── */}
                <div>
                  <label style={{ ...s.label, marginBottom: '0.4rem' }}>Where are you based?</label>
                  <LocationAutocomplete
                    value={profile.location}
                    onChange={val => setProfile({ ...profile, location: val })}
                    placeholder="Tracy, CA"
                    inputStyle={{
                      background: 'var(--cream)',
                      border: '1.5px solid var(--border)',
                      borderRadius: '9px',
                      color: 'var(--text)',
                      paddingLeft: '2.25rem',
                    }}
                  />
                </div>

                <div>
                  <label style={{ ...s.label, marginBottom: '0.4rem' }}>Describe your service in 1–2 sentences</label>
                  <textarea value={profile.bio} onChange={e => setProfile({ ...profile, bio: e.target.value })}
                    placeholder={`Example: I'm a bilingual barber in Tracy specializing in fades and quick appointments. Responsive and reliable service every time.`}
                    rows={4} style={{ ...s.input, resize: 'vertical' }}
                    onFocus={e => { e.target.style.borderColor = 'var(--green)'; e.target.style.boxShadow = '0 0 0 3px rgba(26,92,58,0.1)'; }}
                    onBlur={e => { e.target.style.borderColor = 'var(--border)'; e.target.style.boxShadow = 'none'; }}
                  />
                </div>
                {saved && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--green)', fontSize: '0.875rem', fontWeight: 600 }}>
                    <span style={{ width: '20px', height: '20px', borderRadius: '50%', background: 'var(--green)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem' }}>✓</span>
                    Profile saved!
                  </div>
                )}
                <button onClick={handleSaveProfile} disabled={saving}
                  style={{ background: 'var(--green)', color: 'white', border: 'none', borderRadius: '9px', padding: '0.875rem 2rem', fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Saving...' : 'Save & Continue →'}
                </button>
                <button onClick={handleDeleteProfile}
                  style={{ background: 'transparent', border: '1.5px solid rgba(220,38,38,0.3)', color: '#dc2626', borderRadius: '9px', padding: '0.75rem 1.5rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(220,38,38,0.05)'; e.currentTarget.style.borderColor = '#dc2626'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(220,38,38,0.3)'; }}>
                  Delete Provider Profile
                </button>
                <div style={{ background: 'var(--cream)', borderRadius: '10px', padding: '1rem', border: '1px solid var(--border)' }}>
                  {TIPS.slice(0,2).map((tip,i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: i < 1 ? '0.5rem' : 0 }}>
                      <span style={{ fontSize: '0.9rem' }}>{tip.icon}</span>
                      <p style={{ fontSize: '0.78rem', color: 'var(--text-mid)', lineHeight: 1.4 }}>{tip.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right — live preview */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '80px' }}>
            <div style={{ ...s.card, position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--text)' }}>This is what <em style={{ fontFamily: 'Playfair Display, serif' }}>clients</em> will see</p>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'var(--green)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '0.85rem' }}>✓</div>
              </div>
              <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid var(--border)' }}>
                <div style={{ height: '140px', background: profilePicUrl ? 'transparent' : 'var(--green-pale)', position: 'relative', overflow: 'hidden' }}>
                  {profilePicUrl ? <img src={profilePicUrl} alt="Preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', opacity: 0.3 }}>👤</div>}
                </div>
                <div style={{ padding: '0.875rem', background: 'white' }}>
                  <p style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text)', fontFamily: 'Playfair Display, serif', marginBottom: '0.2rem' }}>{profile.name || 'Your Name'}</p>
                  {profile.location && <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>📍 {profile.location}</p>}
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-mid)' }}>⭐ 150+</span>
                    <span style={{ color: 'var(--border)', fontSize: '0.7rem' }}>|</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-mid)' }}>📋 Bookings Completed</span>
                  </div>
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>⏱ Responds in under 1 hour · 5.0</p>
                  {services.length > 0 && (
                    <div style={{ marginTop: '0.625rem', display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                      {services.slice(0,2).map((svc,i) => (
                        <span key={i} style={{ background: 'var(--green-pale)', color: 'var(--green)', border: '1px solid rgba(26,92,58,0.15)', borderRadius: '6px', fontSize: '0.65rem', fontWeight: 600, padding: '0.15rem 0.5rem' }}>{svc.name}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={{ ...s.card, background: 'var(--cream-dark)' }}>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.875rem' }}>💡 Tips for Your Profile</p>
              {TIPS.map((tip,i) => (
                <div key={i} style={{ display: 'flex', gap: '0.6rem', marginBottom: i < TIPS.length-1 ? '0.625rem' : 0 }}>
                  <span style={{ fontSize: '0.9rem', flexShrink: 0 }}>{tip.icon}</span>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-mid)', lineHeight: 1.5 }}>{tip.text}</p>
                </div>
              ))}
              <div style={{ marginTop: '1rem', paddingTop: '0.875rem', borderTop: '1px solid var(--border)' }}>
                <a href="#" style={{ fontSize: '0.8rem', color: 'var(--green)', fontWeight: 600, textDecoration: 'none' }}>Learn more about making a great profile →</a>
              </div>
            </div>
            <div style={s.card}>
              <p style={{ fontWeight: 700, fontSize: '0.875rem', color: 'var(--text)', marginBottom: '0.875rem' }}>Profile Checklist</p>
              {completionItems.map((item,i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '0.5rem' }}>
                  <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: item.done ? 'var(--green)' : 'var(--cream-mid)', border: `1.5px solid ${item.done ? 'var(--green)' : 'var(--border)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.6rem', color: 'white' }}>
                    {item.done ? '✓' : ''}
                  </div>
                  <span style={{ fontSize: '0.8rem', color: item.done ? 'var(--text-mid)' : 'var(--text-light)', fontWeight: item.done ? 500 : 400 }}>{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── SERVICES TAB ── */}
      {activeTab === 'services' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: '1.5rem', alignItems: 'start' }}>
          {editingService && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,26,24,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem', backdropFilter: 'blur(6px)', overflowY: 'auto' }}>
              <div style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(32px) saturate(200%)', WebkitBackdropFilter: 'blur(32px) saturate(200%)', border: '1px solid rgba(255,255,255,0.95)', borderRadius: '24px', width: '100%', maxWidth: '680px', maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(3,54,111,0.18)' }}>
                <div style={{ background: 'var(--green)', borderRadius: '20px 20px 0 0', padding: '1.5rem 2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', fontWeight: 800, color: 'white', marginBottom: '0.15rem' }}>{editingService.id ? 'Edit Service' : 'Add New Service'}</h2>
                    <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.82rem' }}>Set details and upload photos/videos for this service</p>
                  </div>
                  <button onClick={() => setEditingService(null)} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: '8px', color: 'white', cursor: 'pointer', fontSize: '0.82rem', padding: '0.4rem 0.875rem', fontFamily: 'DM Sans, sans-serif' }}>✕ Cancel</button>
                </div>
                <div style={{ padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                  <div>
                    <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '1rem' }}>Service Details</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={s.label}>Service Name *</label>
                        <input type="text" value={editingService.name} onChange={e => setEditingService(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Fade + Line Up" style={s.input}
                          onFocus={e => { e.target.style.borderColor='var(--green)'; e.target.style.boxShadow='0 0 0 3px rgba(26,92,58,0.1)'; }}
                          onBlur={e => { e.target.style.borderColor='var(--border)'; e.target.style.boxShadow='none'; }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }} ref={serviceRef}>
                        <label style={s.label}>Service Category</label>
                        <div style={{ position: 'relative' }}>
                          <input type="text" placeholder="e.g. ✂️ Barber, 💅 Nail Tech..."
                            value={serviceSearch !== '' ? serviceSearch : (editingService.serviceType || '')}
                            onChange={e => { setServiceSearch(e.target.value); setShowServiceDropdown(true); }}
                            style={{ ...s.input, paddingRight: '2.5rem' }}
                            onFocus={e => { e.target.style.borderColor='var(--green)'; setShowServiceDropdown(true); }}
                            onBlur={e => e.target.style.borderColor='var(--border)'} />
                          <span style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>▾</span>
                          {showServiceDropdown && (
                            <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: 'var(--white)', border: '1.5px solid var(--border-dark)', borderRadius: '10px', maxHeight: '200px', overflowY: 'auto', zIndex: 200, boxShadow: 'var(--shadow-lg)' }}>
                              {SERVICE_TYPES.filter(st => st.toLowerCase().includes(serviceSearch.toLowerCase())).map(st => (
                                <div key={st} onClick={() => { setEditingService(p => ({ ...p, serviceType: st })); setServiceSearch(''); setShowServiceDropdown(false); }}
                                  style={{ padding: '0.65rem 1rem', fontSize: '0.875rem', color: 'var(--text-mid)', cursor: 'pointer', transition: 'background 0.15s', borderBottom: '1px solid var(--cream)' }}
                                  onMouseEnter={e => { e.currentTarget.style.background='var(--green-pale)'; e.currentTarget.style.color='var(--green)'; }}
                                  onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--text-mid)'; }}>{st}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <label style={s.label}>Price ($) *</label>
                        <input type="number" value={editingService.price} onChange={e => setEditingService(p => ({ ...p, price: e.target.value }))} placeholder="e.g. 35" style={s.input}
                          onFocus={e => { e.target.style.borderColor='var(--green)'; e.target.style.boxShadow='0 0 0 3px rgba(26,92,58,0.1)'; }}
                          onBlur={e => { e.target.style.borderColor='var(--border)'; e.target.style.boxShadow='none'; }} />
                      </div>
                      <div>
                        <label style={s.label}>Duration (minutes)</label>
                        <input type="number" value={editingService.duration} onChange={e => setEditingService(p => ({ ...p, duration: e.target.value }))} placeholder="e.g. 45" style={s.input}
                          onFocus={e => { e.target.style.borderColor='var(--green)'; e.target.style.boxShadow='0 0 0 3px rgba(26,92,58,0.1)'; }}
                          onBlur={e => { e.target.style.borderColor='var(--border)'; e.target.style.boxShadow='none'; }} />
                      </div>
                      <div style={{ gridColumn: '1 / -1' }}>
                        <label style={s.label}>Description</label>
                        <textarea value={editingService.description} onChange={e => setEditingService(p => ({ ...p, description: e.target.value }))} placeholder="What's included? What should clients expect?" rows={3}
                          style={{ ...s.input, resize: 'vertical' }}
                          onFocus={e => { e.target.style.borderColor='var(--green)'; e.target.style.boxShadow='0 0 0 3px rgba(26,92,58,0.1)'; }}
                          onBlur={e => { e.target.style.borderColor='var(--border)'; e.target.style.boxShadow='none'; }} />
                      </div>
                    </div>
                  </div>

                  {/* Deposit toggle */}
                  <div style={{ background: 'var(--cream)', borderRadius: '12px', padding: '1.25rem', border: `1.5px solid ${editingService.requireDeposit ? 'rgba(26,92,58,0.3)' : 'var(--border)'}`, transition: 'all 0.2s' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editingService.requireDeposit ? '1rem' : 0 }}>
                      <div>
                        <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text)' }}>Require Deposit</p>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Charge a % upfront to hold the slot. Remaining balance due after service.</p>
                      </div>
                      <div onClick={() => setEditingService(p => ({ ...p, requireDeposit: !p.requireDeposit, depositPercent: p.depositPercent || 25 }))}
                        style={{ width: '44px', height: '24px', borderRadius: '99px', background: editingService.requireDeposit ? 'var(--green)' : 'var(--border-dark)', position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0 }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: editingService.requireDeposit ? '23px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                    </div>
                    {editingService.requireDeposit && (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                          <label style={{ ...s.label, marginBottom: 0 }}>Deposit Amount</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.25rem', fontWeight: 800, color: 'var(--green)' }}>{editingService.depositPercent || 25}%</span>
                            <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>= ${editingService.price ? ((parseFloat(editingService.price)*(editingService.depositPercent||25)/100)).toFixed(2) : '—'} upfront</span>
                          </div>
                        </div>
                        <input type="range" min="10" max="100" step="5" value={editingService.depositPercent || 25}
                          onChange={e => setEditingService(p => ({ ...p, depositPercent: parseInt(e.target.value) }))}
                          style={{ width: '100%', accentColor: 'var(--green)', cursor: 'pointer', marginBottom: '0.5rem' }} />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.68rem', color: 'var(--text-light)' }}>
                          <span>10% min</span><span>100% (full payment)</span>
                        </div>
                        {editingService.price && (
                          <div style={{ marginTop: '0.875rem', background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.2)', borderRadius: '9px', padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between' }}>
                            {[
                              { label: 'Client pays now', val: `$${((parseFloat(editingService.price)*(editingService.depositPercent||25)/100)).toFixed(2)}`, color: 'var(--green)' },
                              { sep: '+' },
                              { label: 'After service', val: `$${(parseFloat(editingService.price)-(parseFloat(editingService.price)*(editingService.depositPercent||25)/100)).toFixed(2)}`, color: 'var(--text)' },
                              { sep: '=' },
                              { label: 'Total', val: `$${parseFloat(editingService.price).toFixed(2)}`, color: 'var(--text)' },
                            ].map((item,i) => item.sep
                              ? <div key={i} style={{ color: 'var(--border)', fontSize: '1.25rem', alignSelf: 'center' }}>{item.sep}</div>
                              : <div key={i} style={{ textAlign: 'center' }}>
                                  <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.2rem' }}>{item.label}</p>
                                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 800, color: item.color }}>{item.val}</p>
                                </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Cancellation Policy */}
                  <div style={{ background: 'var(--cream)', borderRadius: '12px', padding: '1.25rem', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text)', marginBottom: '0.2rem' }}>Cancellation Policy</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>Clients must agree to this policy before booking. Refunds are calculated automatically.</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                      {[
                        { key: 'flexible', icon: '✅', label: 'Flexible',  desc: 'Full refund if cancelled 24+ hours before. No refund after.' },
                        { key: 'moderate', icon: '⚠️', label: 'Moderate',  desc: 'Full refund if cancelled 48+ hours before. 50% refund within 48 hours.' },
                        { key: 'strict',   icon: '❌', label: 'Strict',    desc: 'No refunds. Deposit is non-refundable.' },
                      ].map(opt => {
                        const selected = (editingService.cancellationPolicy || 'flexible') === opt.key;
                        return (
                          <div key={opt.key} onClick={() => setEditingService(p => ({ ...p, cancellationPolicy: opt.key }))}
                            style={{ padding: '0.75rem 1rem', border: `1.5px solid ${selected ? 'var(--green)' : 'var(--border)'}`, borderRadius: '9px', cursor: 'pointer', background: selected ? 'var(--green-pale)' : 'var(--white)', transition: 'all 0.15s', display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                            <span style={{ fontSize: '1rem', marginTop: '0.1rem' }}>{opt.icon}</span>
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: '0.875rem', fontWeight: 700, color: selected ? 'var(--green)' : 'var(--text)', marginBottom: '0.15rem' }}>{opt.label}</p>
                              <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{opt.desc}</p>
                            </div>
                            {selected && <span style={{ width: 18, height: 18, borderRadius: '50%', background: 'var(--green)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.6rem', flexShrink: 0, marginTop: '0.1rem' }}>✓</span>}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Recurring */}
                  <div style={{ background: 'var(--cream)', borderRadius: '12px', padding: '1.25rem', border: '1px solid var(--border)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: editingService.recurring ? '1rem' : 0 }}>
                      <div>
                        <p style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--text)' }}>Recurring Subscription</p>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>Enable for weekly cleaning, monthly maintenance, etc.</p>
                      </div>
                      <div onClick={() => setEditingService(p => ({ ...p, recurring: !p.recurring }))}
                        style={{ width: '44px', height: '24px', borderRadius: '99px', background: editingService.recurring ? 'var(--green)' : 'var(--border-dark)', position: 'relative', transition: 'background 0.2s', cursor: 'pointer', flexShrink: 0 }}>
                        <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: editingService.recurring ? '23px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
                      </div>
                    </div>
                    {editingService.recurring && (
                      <div style={{ display: 'flex', gap: '0.625rem' }}>
                        {['week','biweek','month'].map(iv => (
                          <button key={iv} onClick={() => setEditingService(p => ({ ...p, interval: iv }))}
                            style={{ flex: 1, padding: '0.5rem', borderRadius: '8px', border: `1.5px solid ${editingService.interval===iv?'var(--green)':'var(--border)'}`, background: editingService.interval===iv?'var(--green-pale)':'var(--white)', color: editingService.interval===iv?'var(--green)':'var(--text-muted)', cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem', fontFamily: 'DM Sans, sans-serif' }}>
                            {iv==='week'?'Weekly':iv==='biweek'?'Bi-weekly':'Monthly'}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Service media */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <div>
                        <p style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>Service Photos & Videos</p>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Show clients exactly what this service looks like</p>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600 }}>{(editingService.mediaUrls||[]).length}/12</span>
                        {(editingService.mediaUrls||[]).length > 1 && <span style={{ fontSize: '0.65rem', color: 'var(--green)', fontWeight: 500, fontStyle: 'italic' }}>drag to reorder · first = cover</span>}
                      </div>
                    </div>
                    <div onClick={() => !svcMediaUploading && svcMediaInputRef.current?.click()}
                      style={{ border: '2px dashed rgba(26,92,58,0.3)', borderRadius: '12px', padding: svcMediaUploading ? '1.5rem' : '1.25rem', textAlign: 'center', cursor: svcMediaUploading ? 'not-allowed' : 'pointer', background: 'var(--green-tint)', transition: 'all 0.2s', marginBottom: '1rem' }}
                      onMouseEnter={e => { if(!svcMediaUploading) e.currentTarget.style.borderColor='var(--green)'; }}
                      onMouseLeave={e => e.currentTarget.style.borderColor='rgba(26,92,58,0.3)'}>
                      {svcMediaUploading
                        ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem' }}>
                            <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid var(--green)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
                            <span style={{ fontSize: '0.82rem', color: 'var(--green)', fontWeight: 600 }}>Uploading...</span>
                          </div>
                        : <div>
                            <p style={{ fontWeight: 700, color: 'var(--text)', fontSize: '0.875rem', marginBottom: '0.25rem' }}>📸 Add photos or videos</p>
                            <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>JPG, PNG, WebP, GIF up to 4MB · MP4, MOV, WebM up to 6MB · Max 12 items</p>
                          </div>
                      }
                    </div>
                    <input ref={svcMediaInputRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" multiple onChange={handleSvcMediaUpload} style={{ display: 'none' }} />
                    {svcMediaError && <p style={{ color: svcMediaError.startsWith('⚠️') ? '#dc2626' : 'var(--text-muted)', fontSize: '0.78rem', marginBottom: '0.75rem' }}>{svcMediaError}</p>}
                    {(editingService.mediaUrls||[]).length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(110px, 1fr))', gap: '0.625rem' }}>
                        {(editingService.mediaUrls||[]).map((item,idx) => {
                          const url = typeof item==='string'?item:item.url;
                          const type = typeof item==='object'?item.type:(url.match(/\.(mp4|mov|webm)/i)?'video':'image');
                          const isCover = idx===0; const isOver = dragOver===idx;
                          return (
                            <div key={url+idx} draggable onDragStart={e=>handleDragStart(e,idx)} onDragEnd={handleDragEnd} onDragOver={e=>e.preventDefault()} onDragEnter={()=>handleDragEnter(idx)} onDrop={e=>handleDrop(e,idx)}
                              style={{ position:'relative', aspectRatio:'1', borderRadius:'9px', overflow:'hidden', border: isOver?'2.5px dashed var(--green)':isCover?'2px solid var(--green)':'1px solid var(--border)', background:'var(--cream)', cursor:'grab', transform:isOver?'scale(1.04)':'scale(1)', transition:'transform 0.15s, border 0.15s' }}
                              onMouseEnter={e=>{ e.currentTarget.querySelector('.del-btn').style.opacity='1'; e.currentTarget.querySelector('.drag-hint').style.opacity='1'; }}
                              onMouseLeave={e=>{ e.currentTarget.querySelector('.del-btn').style.opacity='0'; e.currentTarget.querySelector('.drag-hint').style.opacity='0'; }}>
                              {type==='video'
                                ?<video src={url} style={{ width:'100%',height:'100%',objectFit:'cover' }} muted playsInline onMouseEnter={e=>e.target.play()} onMouseLeave={e=>{e.target.pause();e.target.currentTime=0;}}/>
                                :<img src={url} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }} draggable={false}/>
                              }
                              {isCover&&<div style={{ position:'absolute',bottom:4,left:4,background:'var(--green)',color:'white',borderRadius:'4px',fontSize:'0.55rem',fontWeight:800,padding:'2px 6px',letterSpacing:'0.06em',textTransform:'uppercase',pointerEvents:'none' }}>Cover</div>}
                              {type==='video'&&<div style={{ position:'absolute',bottom:isCover?22:4,left:4,background:'rgba(0,0,0,0.55)',color:'white',borderRadius:'4px',fontSize:'0.55rem',fontWeight:700,padding:'1px 5px',pointerEvents:'none' }}>▶</div>}
                              <div className="drag-hint" style={{ position:'absolute',top:4,left:4,color:'rgba(255,255,255,0.85)',fontSize:'0.75rem',opacity:0,transition:'opacity 0.15s',pointerEvents:'none',textShadow:'0 1px 3px rgba(0,0,0,0.6)' }}>⠿</div>
                              <button className="del-btn" onClick={()=>handleDeleteSvcMedia(idx)} style={{ position:'absolute',top:'4px',right:'4px',width:22,height:22,borderRadius:'50%',background:'rgba(220,38,38,0.85)',border:'none',color:'white',fontSize:'0.65rem',cursor:'pointer',opacity:0,transition:'opacity 0.15s',display:'flex',alignItems:'center',justifyContent:'center' }}>✕</button>
                            </div>
                          );
                        })}
                        {(editingService.mediaUrls||[]).length < 12 && (
                          <div onClick={()=>svcMediaInputRef.current?.click()}
                            style={{ aspectRatio:'1',borderRadius:'9px',border:'2px dashed rgba(26,92,58,0.25)',background:'var(--green-tint)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',fontSize:'1.25rem',color:'var(--green)',transition:'all 0.15s',opacity:0.6 }}
                            onMouseEnter={e=>{e.currentTarget.style.opacity='1';e.currentTarget.style.borderColor='var(--green)';}}
                            onMouseLeave={e=>{e.currentTarget.style.opacity='0.6';e.currentTarget.style.borderColor='rgba(26,92,58,0.25)';}}>+</div>
                        )}
                      </div>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem', borderTop: '1px solid var(--border)' }}>
                    <button onClick={() => setEditingService(null)} className="btn-secondary" style={{ flex: 1 }}>Cancel</button>
                    <button onClick={handleSaveEditingService} disabled={!editingService.name||!editingService.price}
                      style={{ flex: 2, background: editingService.name&&editingService.price?'var(--green)':'var(--cream-mid)', color: editingService.name&&editingService.price?'white':'var(--text-light)', border: 'none', borderRadius: '9px', padding: '0.875rem', fontWeight: 700, fontSize: '0.95rem', cursor: editingService.name&&editingService.price?'pointer':'not-allowed', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s' }}>
                      {editingService.id ? '✓ Save Changes' : '+ Add Service'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={s.card}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                <div>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.1rem' }}>Your Offerings</p>
                  <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{services.length} service{services.length!==1?'s':''} · click to edit</p>
                </div>
                <button onClick={openNewService} style={{ background: 'var(--green)', color: 'white', border: 'none', borderRadius: '8px', padding: '0.6rem 1.25rem', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', display: 'flex', alignItems: 'center', gap: '0.4rem', transition: 'all 0.2s' }}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--green-dark)'} onMouseLeave={e=>e.currentTarget.style.background='var(--green)'}>+ Add Service</button>
              </div>
              {services.length===0 ? (
                <div style={{ textAlign:'center', padding:'3rem 1rem', border:'2px dashed rgba(26,92,58,0.2)', borderRadius:'12px', cursor:'pointer', background:'var(--green-tint)' }} onClick={openNewService}
                  onMouseEnter={e=>e.currentTarget.style.borderColor='var(--green)'} onMouseLeave={e=>e.currentTarget.style.borderColor='rgba(26,92,58,0.2)'}>
                  <p style={{ fontSize:'2rem', marginBottom:'0.5rem', opacity:0.4 }}>✦</p>
                  <p style={{ fontWeight:700, color:'var(--text)', marginBottom:'0.3rem' }}>No services yet</p>
                  <p style={{ fontSize:'0.82rem', color:'var(--text-muted)' }}>Click to add your first service</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                  {services.map(svc => {
                    const mediaCount = (svc.mediaUrls||[]).length;
                    return (
                      <div key={svc.id} onClick={()=>openEditService(svc)}
                        style={{ background:'rgba(255,255,255,0.5)', backdropFilter:'blur(14px)', WebkitBackdropFilter:'blur(14px)', border:'1px solid rgba(255,255,255,0.75)', borderRadius:'14px', padding:'1rem 1.25rem', cursor:'pointer', transition:'all 0.2s', display:'flex', gap:'1rem', alignItems:'stretch' }}
                        onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(26,92,58,0.3)';e.currentTarget.style.background='var(--green-tint)';}}
                        onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--cream)';}}>
                        {mediaCount>0&&(
                          <div style={{ width:'64px', flexShrink:0, display:'flex', flexDirection:'column', gap:'3px' }}>
                            {(svc.mediaUrls||[]).slice(0,3).map((item,i)=>{
                              const url=typeof item==='string'?item:item.url;
                              const isVid=typeof item==='object'?item.type==='video':url.match(/\.(mp4|mov|webm)/i);
                              return <div key={i} style={{ flex:1, borderRadius:'6px', overflow:'hidden', background:'var(--border)', position:'relative', minHeight:'20px' }}>
                                {isVid?<div style={{ width:'100%',height:'100%',background:'#1a5c3a',display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.6rem',color:'white' }}>▶</div>:<img src={url} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }}/>}
                              </div>;
                            })}
                          </div>
                        )}
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.2rem' }}>
                            <h4 style={{ fontSize:'0.95rem', fontWeight:700, color:'var(--text)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{svc.name}</h4>
                            {svc.serviceType&&<span style={{ background:'var(--cream-dark)', border:'1px solid var(--border)', color:'var(--text-mid)', borderRadius:'5px', fontSize:'0.62rem', fontWeight:600, padding:'0.1rem 0.45rem', flexShrink:0, whiteSpace:'nowrap', overflow:'hidden', maxWidth:'120px', textOverflow:'ellipsis' }}>{svc.serviceType}</span>}
                            {svc.recurring&&<span style={{ background:'var(--green-pale)', color:'var(--green)', border:'1px solid rgba(26,92,58,0.2)', borderRadius:'20px', fontSize:'0.62rem', fontWeight:700, padding:'0.1rem 0.45rem', flexShrink:0 }}>↻ {intervalLabel(svc.interval)}</span>}
                          </div>
                          {svc.description&&<p style={{ fontSize:'0.75rem', color:'var(--text-muted)', marginBottom:'0.25rem', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{svc.description}</p>}
                          <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', flexWrap:'wrap' }}>
                            <span style={{ fontSize:'0.85rem', fontWeight:800, color:'var(--green)', fontFamily:'Playfair Display, serif' }}>${svc.price}{svc.recurring?`/${svc.interval==='biweek'?'bi-week':svc.interval}`:''}</span>
                            {svc.duration&&<span style={{ fontSize:'0.75rem', color:'var(--text-muted)' }}>{svc.duration} min</span>}
                            {mediaCount>0&&<span style={{ fontSize:'0.68rem', color:'var(--text-muted)', background:'var(--cream-dark)', borderRadius:'5px', padding:'0.1rem 0.45rem' }}>📸 {mediaCount} media</span>}
                          </div>
                        </div>
                        <div style={{ display:'flex', flexDirection:'column', justifyContent:'space-between', alignItems:'flex-end', flexShrink:0, gap:'0.5rem' }}>
                          <span style={{ fontSize:'0.7rem', color:'var(--green)', fontWeight:600 }}>Edit ✎</span>
                          <button onClick={e=>{e.stopPropagation();handleDeleteService(svc.id);}}
                            style={{ background:'transparent', border:'1.5px solid rgba(220,38,38,0.2)', color:'#dc2626', borderRadius:'6px', padding:'0.25rem 0.625rem', fontSize:'0.72rem', fontWeight:600, cursor:'pointer', fontFamily:'DM Sans, sans-serif', transition:'all 0.15s' }}
                            onMouseEnter={e=>{e.currentTarget.style.background='rgba(220,38,38,0.06)';e.currentTarget.style.borderColor='#dc2626';}}
                            onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.borderColor='rgba(220,38,38,0.2)';}}>Delete</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:'1rem', position:'sticky', top:'80px' }}>
            <div style={s.card}>
              <p style={{ fontSize:'0.875rem', fontWeight:700, color:'var(--text)', marginBottom:'1rem' }}>How clients will see your <em style={{ fontFamily:'Playfair Display, serif' }}>services</em></p>
              {services.length===0 ? (
                <div style={{ textAlign:'center', padding:'2rem', color:'var(--text-light)' }}>
                  <p style={{ fontSize:'2rem', marginBottom:'0.5rem', opacity:0.4 }}>✦</p>
                  <p style={{ fontSize:'0.82rem' }}>Add your first service to see a preview</p>
                </div>
              ) : (
                <div style={{ display:'flex', flexDirection:'column', gap:'0.625rem' }}>
                  {services.map((svc,i)=>{
                    const thumb=(svc.mediaUrls||[])[0];
                    const thumbUrl=thumb?(typeof thumb==='string'?thumb:thumb.url):null;
                    return (
                      <div key={i} style={{ background:'var(--cream)', border:'1px solid var(--border)', borderRadius:'10px', overflow:'hidden', display:'flex' }}>
                        {thumbUrl&&<div style={{ width:'52px', flexShrink:0 }}><img src={thumbUrl} alt="" style={{ width:'100%',height:'100%',objectFit:'cover' }}/></div>}
                        <div style={{ flex:1, padding:'0.75rem 0.875rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                          <div>
                            <p style={{ fontSize:'0.82rem', fontWeight:700, color:'var(--text)', marginBottom:'0.1rem' }}>{svc.name}</p>
                            <p style={{ fontSize:'0.68rem', color:'var(--text-muted)' }}>{svc.duration?`${svc.duration} min`:'Duration not set'}</p>
                          </div>
                          <p style={{ fontSize:'0.95rem', fontWeight:800, color:'var(--green)', fontFamily:'Playfair Display, serif' }}>${svc.price}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Online Payment Only toggle */}
            <div style={{ ...s.card, border:`1.5px solid ${onlinePaymentOnly?'rgba(26,92,58,0.3)':'var(--border)'}`, background:onlinePaymentOnly?'var(--green-pale)':'var(--white)', transition:'all 0.2s' }}>
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:'1rem' }}>
                <div>
                  <p style={{ fontSize:'0.875rem', fontWeight:700, color:'var(--text)', marginBottom:'0.3rem' }}>💳 Require Online Payment</p>
                  <p style={{ fontSize:'0.75rem', color:'var(--text-muted)', lineHeight:1.55 }}>When on, clients must pay via Stripe checkout. The "Pay in Person" option will be hidden.</p>
                </div>
                <div onClick={()=>{ const next=!onlinePaymentOnly; setOnlinePaymentOnly(next); saveProviderToDB(profile,services,null,null,next); }}
                  style={{ width:'44px', height:'24px', borderRadius:'99px', background:onlinePaymentOnly?'var(--green)':'var(--border-dark)', position:'relative', transition:'background 0.2s', cursor:'pointer', flexShrink:0 }}>
                  <div style={{ width:'18px', height:'18px', borderRadius:'50%', background:'white', position:'absolute', top:'3px', left:onlinePaymentOnly?'23px':'3px', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }}/>
                </div>
              </div>
              {onlinePaymentOnly&&(
                <div style={{ marginTop:'0.875rem', paddingTop:'0.875rem', borderTop:'1px solid rgba(26,92,58,0.15)', display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  <span style={{ width:18, height:18, borderRadius:'50%', background:'var(--green)', color:'white', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.6rem', flexShrink:0 }}>✓</span>
                  <span style={{ fontSize:'0.78rem', color:'var(--green)', fontWeight:600 }}>Online payment required — "Pay in Person" is hidden from clients</span>
                </div>
              )}
            </div>

            <div style={{ ...s.card, background:'var(--green)', color:'white' }}>
              <p style={{ fontWeight:800, fontSize:'0.9rem', marginBottom:'0.875rem', fontFamily:'Playfair Display, serif' }}>💡 Service Tips</p>
              {['Add photos to each service — it builds trust instantly','"Fade + Line Up" books better than just "Haircut"','Short videos of your work get 5x more engagement','Offer 2–4 services without overwhelming clients'].map((tip,i)=>(
                <p key={i} style={{ fontSize:'0.78rem', opacity:0.85, lineHeight:1.5, marginBottom:i<3?'0.5rem':0 }}>• {tip}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── DEALS TAB ── */}
      {activeTab === 'deals' && (
        <div style={{ maxWidth: 800 }}>
          <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1.75rem', boxShadow: 'var(--shadow-sm)' }}>
            <DealsManager providerId={userId} />
          </div>
        </div>
      )}

      {/* ── PAYMENTS TAB ── */}
      {activeTab === 'payments' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: '1.5rem', alignItems: 'start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div style={{ background: connectStatus?.onboardingComplete ? 'rgba(3,54,111,0.8)' : 'rgba(10,20,40,0.75)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.15)', padding: '2rem 2.5rem', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -60, right: -60, width: 220, height: 220, background: 'radial-gradient(circle, rgba(255,255,255,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <div style={{ width: 48, height: 48, borderRadius: '12px', background: connectStatus?.onboardingComplete ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem' }}>{connectStatus?.onboardingComplete ? '✓' : '💳'}</div>
                  <div>
                    <p style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.2rem' }}>{connectStatus?.onboardingComplete ? 'Stripe Verified' : connectStatus?.connected ? 'Setup Incomplete' : 'Not Connected'}</p>
                    <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.4rem', fontWeight: 800, color: 'white', lineHeight: 1.2 }}>{connectStatus?.onboardingComplete ? 'Payouts Active' : 'Connect Your Bank Account'}</h2>
                  </div>
                </div>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.875rem', marginBottom: '1.5rem', maxWidth: '480px', lineHeight: 1.6 }}>{connectStatus?.onboardingComplete ? 'Payments go directly to your bank account after each booking. Your Stripe account is verified and ready.' : 'To receive payouts from your bookings, connect your bank account through Stripe. Takes about 5 minutes.'}</p>
                {connectStatus?.onboardingComplete ? (
                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                    <button onClick={handleOpenStripeDashboard} style={{ background: 'white', color: 'var(--green)', border: 'none', borderRadius: '9px', padding: '0.75rem 1.5rem', fontWeight: 700, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Open Stripe Dashboard ↗</button>
                    <button onClick={() => fetchBalance(userId)} style={{ background: 'rgba(255,255,255,0.15)', color: 'white', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '9px', padding: '0.75rem 1.25rem', fontWeight: 600, fontSize: '0.875rem', cursor: 'pointer', fontFamily: 'DM Sans, sans-serif' }}>Refresh Balance</button>
                  </div>
                ) : (
                  <button onClick={handleConnectStripe} disabled={connectLoading} style={{ background: 'white', color: '#1e293b', border: 'none', borderRadius: '9px', padding: '0.875rem 2rem', fontWeight: 700, fontSize: '0.95rem', cursor: connectLoading ? 'not-allowed' : 'pointer', fontFamily: 'DM Sans, sans-serif', opacity: connectLoading ? 0.7 : 1 }}>
                    {connectLoading ? '⏳ Loading...' : '🔗 Connect Bank Account via Stripe'}
                  </button>
                )}
              </div>
            </div>

            {connectStatus?.onboardingComplete && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ ...s.card, background: 'var(--green)', color: 'white' }}>
                  <p style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.65)', marginBottom: '0.5rem' }}>Available to Pay Out</p>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 800, color: 'white', lineHeight: 1 }}>{balanceLoading ? '...' : `$${(balance?.available||0).toFixed(2)}`}</p>
                  <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.55)', marginTop: '0.4rem' }}>Ready to withdraw now</p>
                </div>
                <div style={s.card}>
                  <p style={{ fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>Pending</p>
                  <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '2rem', fontWeight: 800, color: 'var(--text)', lineHeight: 1 }}>{balanceLoading ? '...' : `$${(balance?.pending||0).toFixed(2)}`}</p>
                  <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.4rem' }}>Processing, releases soon</p>
                </div>
              </div>
            )}

            {connectStatus?.onboardingComplete && (
              <div style={s.card}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.1rem', fontWeight: 800, color: 'var(--text)', marginBottom: '0.25rem' }}>Request a Payout</p>
                <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '1.5rem' }}>Withdraw your available balance to your bank account.</p>
                <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'flex-end' }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ ...s.label, marginBottom: '0.4rem' }}>Amount ($)</label>
                    <div style={{ position: 'relative' }}>
                      <span style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontWeight: 700, fontSize: '1rem' }}>$</span>
                      <input type="number" value={payoutAmount} onChange={e => { setPayoutAmount(e.target.value); setPayoutError(''); setPayoutResult(null); }} placeholder="0.00" min="1" step="0.01" style={{ ...s.input, paddingLeft: '2rem' }}
                        onFocus={e => { e.target.style.borderColor='var(--green)'; e.target.style.boxShadow='0 0 0 3px rgba(26,92,58,0.1)'; }}
                        onBlur={e => { e.target.style.borderColor='var(--border)'; e.target.style.boxShadow='none'; }} />
                    </div>
                    {balance?.available > 0 && <button onClick={() => setPayoutAmount(balance.available.toFixed(2))} style={{ background: 'none', border: 'none', color: 'var(--green)', fontSize: '0.72rem', fontWeight: 700, cursor: 'pointer', padding: '0.25rem 0', fontFamily: 'DM Sans, sans-serif' }}>Use max (${balance.available.toFixed(2)})</button>}
                  </div>
                </div>
                {payoutError && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '9px', padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#dc2626', marginBottom: '1rem' }}>⚠️ {payoutError}</div>}
                {payoutResult && (
                  <div style={{ background: 'var(--green-pale)', border: '1px solid rgba(26,92,58,0.2)', borderRadius: '9px', padding: '0.875rem 1rem', marginBottom: '1rem' }}>
                    <p style={{ fontSize: '0.875rem', fontWeight: 700, color: 'var(--green)', marginBottom: '0.25rem' }}>✓ {payoutResult.message}</p>
                    <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Amount: ${payoutResult.amount?.toFixed(2)} · Arrival: {payoutResult.arrival}</p>
                    {payoutResult.fee > 0 && <p style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.2rem' }}>Instant fee: ${payoutResult.fee?.toFixed(2)}</p>}
                  </div>
                )}
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => handleRequestPayout(false)} disabled={payoutLoading||!payoutAmount} style={{ flex: 1, background: payoutAmount?'var(--green)':'var(--cream-mid)', color: payoutAmount?'white':'var(--text-light)', border: 'none', borderRadius: '9px', padding: '0.875rem', fontWeight: 700, fontSize: '0.875rem', cursor: payoutAmount?'pointer':'not-allowed', fontFamily: 'DM Sans, sans-serif', opacity: payoutLoading?0.7:1 }}>
                    {payoutLoading ? 'Processing...' : '🏦 Standard Payout (Free · 2–3 days)'}
                  </button>
                  <button onClick={() => handleRequestPayout(true)} disabled={payoutLoading||!payoutAmount} style={{ flex: 1, background: payoutAmount?'#1e293b':'var(--cream-mid)', color: payoutAmount?'white':'var(--text-light)', border: 'none', borderRadius: '9px', padding: '0.875rem', fontWeight: 700, fontSize: '0.875rem', cursor: payoutAmount?'pointer':'not-allowed', fontFamily: 'DM Sans, sans-serif', opacity: payoutLoading?0.7:1 }}>
                    ⚡ Instant (1.25% fee · minutes)
                  </button>
                </div>
              </div>
            )}

            {!connectStatus?.onboardingComplete && (
              <div style={s.card}>
                <p style={{ fontFamily: 'Playfair Display, serif', fontSize: '1rem', fontWeight: 800, color: 'var(--text)', marginBottom: '1.25rem' }}>How payouts work on Vyblo</p>
                {[
                  { icon: '💳', title: 'Client pays online', body: "When a client pays, money goes to Vyblo's Stripe account first — not directly to you." },
                  { icon: '🔒', title: 'Funds are held securely', body: 'Stripe holds the payment while the service is completed. This protects both you and the client.' },
                  { icon: '✅', title: 'Mark job complete', body: 'After you complete the service, mark it done in your dashboard to release the funds.' },
                  { icon: '🏦', title: 'Payout to your bank', body: 'Funds arrive in your bank within 2–3 business days, or instantly for a small 1.25% fee.' },
                ].map((step,i) => (
                  <div key={i} style={{ display:'flex', gap:'1rem', marginBottom:i<3?'1.25rem':0, paddingBottom:i<3?'1.25rem':0, borderBottom:i<3?'1px solid var(--border)':'none' }}>
                    <div style={{ width:40, height:40, borderRadius:'10px', background:'var(--green-pale)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', flexShrink:0 }}>{step.icon}</div>
                    <div>
                      <p style={{ fontSize:'0.875rem', fontWeight:700, color:'var(--text)', marginBottom:'0.2rem' }}>{step.title}</p>
                      <p style={{ fontSize:'0.78rem', color:'var(--text-muted)', lineHeight:1.55 }}>{step.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Right panel */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', position: 'sticky', top: '80px' }}>
            <div style={{ ...s.card, border:`1.5px solid ${verified?'rgba(26,92,58,0.3)':'var(--border)'}`, background:verified?'var(--green-pale)':'var(--white)', transition:'all 0.2s' }}>
              <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:'0.75rem', marginBottom:'0.875rem' }}>
                <div>
                  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.25rem' }}>
                    <p style={{ fontSize:'0.875rem', fontWeight:700, color:verified?'var(--green)':'var(--text)' }}>{verified?'✓ Identity Verified':'🪪 Get Verified'}</p>
                    {verified&&<span style={{ background:'var(--green)', color:'white', borderRadius:'20px', fontSize:'0.6rem', fontWeight:800, padding:'0.1rem 0.5rem', letterSpacing:'0.04em' }}>VERIFIED</span>}
                  </div>
                  <p style={{ fontSize:'0.75rem', color:'var(--text-muted)', lineHeight:1.55 }}>{verified?'Your profile shows a ✓ Verified badge to clients. This builds trust and increases bookings.':'Verify your identity with a government ID. Takes ~2 min. Adds a ✓ badge to your profile that clients can see.'}</p>
                </div>
                {verified&&<div style={{ width:40, height:40, borderRadius:'50%', background:'var(--green)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1.1rem', color:'white', flexShrink:0 }}>✓</div>}
              </div>
              {!verified&&(
                <>
                  <button onClick={handleGetVerified} disabled={verifyLoading} style={{ width:'100%', background:'var(--green)', color:'white', border:'none', borderRadius:'9px', padding:'0.75rem', fontWeight:700, fontSize:'0.875rem', cursor:verifyLoading?'not-allowed':'pointer', fontFamily:'DM Sans, sans-serif', opacity:verifyLoading?0.7:1, display:'flex', alignItems:'center', justifyContent:'center', gap:'0.5rem' }}>{verifyLoading?'⏳ Loading...':'🪪 Verify My Identity →'}</button>
                  <p style={{ fontSize:'0.68rem', color:'var(--text-muted)', marginTop:'0.625rem', textAlign:'center', lineHeight:1.5 }}>Powered by Stripe Identity · Secure &amp; encrypted</p>
                </>
              )}
            </div>

            <div style={s.card}>
              <p style={{ fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-muted)', marginBottom:'1rem' }}>Account Status</p>
              {[
                { label:'Stripe Connected', done:connectStatus?.connected },
                { label:'Identity Verified', done:connectStatus?.onboardingComplete },
                { label:'Charges Enabled', done:connectStatus?.chargesEnabled },
                { label:'Payouts Enabled', done:connectStatus?.payoutsEnabled },
                { label:'ID Verified (Vyblo)', done:verified },
              ].map((item,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'0.625rem' }}>
                  <span style={{ fontSize:'0.82rem', color:'var(--text-mid)' }}>{item.label}</span>
                  <span style={{ fontSize:'0.7rem', fontWeight:700, color:item.done?'var(--green)':'var(--text-light)', background:item.done?'var(--green-pale)':'var(--cream-mid)', border:`1px solid ${item.done?'rgba(26,92,58,0.2)':'var(--border)'}`, borderRadius:'20px', padding:'0.1rem 0.6rem' }}>{item.done?'✓ Active':'○ Pending'}</span>
                </div>
              ))}
              {connectStatus?.email&&<div style={{ marginTop:'0.875rem', paddingTop:'0.875rem', borderTop:'1px solid var(--border)' }}><p style={{ fontSize:'0.72rem', color:'var(--text-muted)' }}>Stripe account: <strong style={{ color:'var(--text)' }}>{connectStatus.email}</strong></p></div>}
            </div>

            <div style={{ ...s.card, background:'var(--cream-dark)' }}>
              <p style={{ fontWeight:700, fontSize:'0.875rem', color:'var(--text)', marginBottom:'0.875rem' }}>🔒 Your money is protected</p>
              {['Stripe holds funds — not individuals','Bank-level 256-bit encryption','Instant payouts available 24/7','Dispute protection built in','Stripe is PCI DSS compliant'].map((tip,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:'0.5rem', marginBottom:'0.5rem' }}>
                  <span style={{ width:16, height:16, borderRadius:'50%', background:'var(--green)', color:'white', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'0.5rem', flexShrink:0 }}>✓</span>
                  <span style={{ fontSize:'0.78rem', color:'var(--text-mid)' }}>{tip}</span>
                </div>
              ))}
            </div>

            <div style={s.card}>
              <p style={{ fontWeight:700, fontSize:'0.875rem', color:'var(--text)', marginBottom:'0.875rem' }}>Fee Breakdown</p>
              {[{label:'Stripe processing',value:'2.9% + 30¢'},{label:'Vyblo platform fee',value:'0%*'},{label:'Standard payout',value:'Free · 2–3 days'},{label:'Instant payout',value:'1.25% · minutes'}].map((row,i)=>(
                <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'0.5rem 0.625rem', background:i%2===0?'var(--cream)':'transparent', borderRadius:'7px', marginBottom:'0.2rem' }}>
                  <span style={{ fontSize:'0.78rem', color:'var(--text-muted)' }}>{row.label}</span>
                  <span style={{ fontSize:'0.78rem', fontWeight:700, color:'var(--text)' }}>{row.value}</span>
                </div>
              ))}
              <p style={{ fontSize:'0.68rem', color:'var(--text-light)', marginTop:'0.75rem' }}>* Platform fee currently waived during beta</p>
            </div>
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default ProviderSettings;